import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  ThemeColors, lightColors, darkColors,
  Typography, typography as sharedTypography,
  Spacing, Sizes, Radii, Shadows, Motion, Opacity,
  spacing, sizes, radii, shadows, shadowsDark, motion, opacity,
} from '@chordbaker/design';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface Theme {
  colors: ThemeColors;
  spacing: Spacing;
  typography: Typography;
  sizes: Sizes;
  radii: Radii;
  shadows: Shadows;
  motion: Motion;
  opacity: Opacity;
  isDark: boolean;
}

const sharedTokens = {
  spacing,
  sizes,
  radii,
  motion,
  opacity,
  typography: sharedTypography,
};

const lightTheme: Theme = {
  colors: lightColors,
  shadows,
  ...sharedTokens,
  isDark: false,
};

const darkTheme: Theme = {
  colors: darkColors,
  shadows: shadowsDark,
  ...sharedTokens,
  isDark: true,
};

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = 'chordbaker_theme_mode';

// Web-specific function to detect system color scheme
const getSystemColorScheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
  const [systemColorScheme, setSystemColorScheme] = useState<'light' | 'dark'>(
    getSystemColorScheme()
  );

  // Load saved theme preference on mount
  useEffect(() => {
    const loadThemePreference = () => {
      try {
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          setThemeModeState(savedTheme as ThemeMode);
        }
      } catch (error) {
        console.warn('Failed to load theme preference:', error);
      }
    };

    loadThemePreference();
  }, []);

  // Listen for system theme changes (Web-specific)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        setSystemColorScheme(e.matches ? 'dark' : 'light');
      };

      // For older browsers
      if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
      // For modern browsers
      else if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
    }
  }, []);

  // Save theme preference when it changes
  const setThemeMode = (mode: ThemeMode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
      setThemeModeState(mode);
    } catch (error) {
      console.warn('Failed to save theme preference:', error);
      setThemeModeState(mode); // Still update the state even if save fails
    }
  };

  // Determine current theme based on mode and system preference
  const currentTheme = React.useMemo(() => {
    if (themeMode === 'system') {
      return systemColorScheme === 'dark' ? darkTheme : lightTheme;
    }
    return themeMode === 'dark' ? darkTheme : lightTheme;
  }, [themeMode, systemColorScheme]);

  // Update mobile browser theme colors when theme changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Update theme-color meta tag for Android Chrome, etc.
      const themeColorMeta = document.getElementById('theme-color-meta') as HTMLMetaElement;
      if (themeColorMeta) {
        themeColorMeta.content = currentTheme.colors.background;
      }

      // Update Microsoft browsers theme color
      const msThemeColorMeta = document.getElementById('ms-theme-color-meta') as HTMLMetaElement;
      if (msThemeColorMeta) {
        msThemeColorMeta.content = currentTheme.colors.background;
      }

      // Use black-translucent for all themes to ensure consistent background
      const appleStatusBarMeta = document.getElementById('apple-status-bar-meta') as HTMLMetaElement;
      if (appleStatusBarMeta) {
        appleStatusBarMeta.content = 'black-translucent';
      }

      // Simple fix: just update body and html background
      const backgroundColor = currentTheme.colors.background;
      document.body.style.backgroundColor = backgroundColor;
      document.documentElement.style.backgroundColor = backgroundColor;
    }
  }, [currentTheme]);

  const value: ThemeContextType = {
    theme: currentTheme,
    themeMode,
    setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export { lightTheme, darkTheme };
